import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { api } from '../api/client.js'
import { useApi } from '../api/useApi.js'
import Footer from '../components/Footer.jsx'
import Header from '../components/Header.jsx'
import { PlayIcon } from '../components/Icons.jsx'
import LiveBadge from '../components/LiveBadge.jsx'
import DonateButton from '../components/DonateButton.jsx'
import { pickI18n } from '../i18n'

const Section = ({ className = '', children }) => (
  <section className={`mx-auto w-full max-w-7xl px-4 sm:px-8 ${className}`}>{children}</section>
)

function WatchLiveButton() {
  const { t } = useTranslation()
  return (
    <Link
      to="/live"
      className="flex items-center gap-3 rounded-full bg-surface px-[26px] py-[18px] text-lg font-extrabold text-ink no-underline transition hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(217,58,58,0.25)]"
    >
      <span className="up-dot size-3 rounded-full bg-live" />
      {t('home.watchLive')}
      <span className="flex h-4 items-end gap-[3px]" aria-hidden="true">
        {[0, 0.25, 0.5].map((d) => (
          <span key={d} className="up-eq h-4 w-[3px] rounded-sm bg-live" style={{ animationDelay: `${d}s` }} />
        ))}
      </span>
    </Link>
  )
}

function LiveCard({ feeder }) {
  const { t, i18n } = useTranslation()
  if (!feeder) return null
  return (
    <Link
      to={`/feeders/${feeder.slug}`}
      className="up-float absolute right-0 bottom-2 w-[250px] overflow-hidden rounded-[20px] bg-surface text-ink no-underline shadow-[0_16px_40px_rgba(30,42,53,0.16)]"
    >
      <div className="relative flex aspect-[16/10] items-center justify-center bg-slate">
        {feeder.photo_url && (
          <img src={feeder.photo_url} alt={t('home.liveAlt')} className="absolute inset-0 size-full object-cover" />
        )}
        {feeder.is_online && (
          <span className="absolute top-2.5 left-2.5">
            <LiveBadge size="sm" />
          </span>
        )}
        <span className="relative flex size-11 items-center justify-center rounded-full bg-surface/90 text-ink">
          <PlayIcon />
        </span>
      </div>
      <div className="flex items-center justify-between gap-2 px-3.5 py-3">
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-[15px] font-extrabold">{pickI18n(feeder.name, i18n.resolvedLanguage)}</span>
          <span className="text-[13px] font-semibold text-subtle">{t('home.cam', { city: feeder.city ?? '' })}</span>
        </div>
        <span className={`text-[13px] font-extrabold ${feeder.is_online ? 'text-teal' : 'text-faint'}`}>
          {feeder.is_online ? t('common.online') : t('common.offline')}
        </span>
      </div>
    </Link>
  )
}

export default function HomePage() {
  const { t } = useTranslation()
  const feeders = useApi(() => api.feeders())
  const cats = useApi(() => api.cats())
  const list = feeders.data ?? []
  const featured = list.find((f) => f.is_online) ?? list[0]

  const facts = [
    { value: feeders.loading ? '—' : list.length, label: t('home.facts.feeders', { count: list.length || 1 }) },
    { value: cats.data?.length ?? '—', label: t('home.facts.cats') },
    { value: '24/7', label: t('home.facts.camera') },
  ]
  const steps = [1, 2, 3].map((n) => ({ n, title: t(`home.how.s${n}t`), text: t(`home.how.s${n}d`) }))

  return (
    <div className="flex min-h-screen flex-col">
      {/* HERO */}
      <div className="p-2 sm:p-4">
        <div className="overflow-hidden rounded-[32px] bg-mint">
          <Header variant="hero" />
          <section className="mx-auto flex w-full max-w-7xl flex-wrap items-center gap-12 px-4 pt-6 pb-14 sm:px-8 sm:pt-10 sm:pb-[72px]">
            <div className="flex flex-[1_1_480px] flex-col gap-7">
              <h1 className="text-[clamp(40px,6vw,76px)] leading-none font-black tracking-[-2.2px] text-balance">
                {t('home.titleA')} <span className="text-teal">{t('home.titleB')}</span>
              </h1>
              <p className="max-w-[520px] text-xl leading-normal text-body">{t('home.lead')}</p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/live"
                  className="rounded-full bg-teal px-[30px] py-[18px] text-lg font-extrabold text-on-teal no-underline transition hover:bg-teal-dark"
                >
                  {t('home.feedNow')}
                </Link>
                <WatchLiveButton />
              </div>
            </div>
            <div className="relative flex min-h-[460px] flex-[1_1_420px] items-center justify-center">
              <div className="relative flex aspect-square w-[min(440px,100%)] items-center justify-center rounded-full bg-surface">
                <span className="up-ring absolute inset-0 rounded-full border-2 border-teal-ring" />
                <img src="/brand/urbanpurr-mark.svg" alt={t('home.heroAlt')} className="up-float-slow h-auto w-[78%]" />
              </div>
              <LiveCard feeder={featured} />
            </div>
          </section>
        </div>
      </div>

      {/* FACTS */}
      <Section className="pt-12 pb-6">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(240px,100%),1fr))] gap-4">
          {facts.map((f) => (
            <div key={f.label} className="flex flex-col gap-1 rounded-[20px] border-[1.5px] border-line p-6">
              <span className="text-[40px] font-black text-teal">{f.value}</span>
              <span className="text-base font-bold text-muted">{f.label}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section className="flex flex-col gap-8 py-14">
        <h2 className="max-w-[640px] text-[clamp(30px,3.5vw,44px)] font-black tracking-[-1px] text-balance">
          {t('home.how.title')}
        </h2>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(260px,100%),1fr))] gap-5">
          {steps.map((s) => (
            <div key={s.n} className="flex flex-col gap-3.5 rounded-3xl bg-sand p-7">
              <span className="text-[15px] font-black text-teal">0{s.n}</span>
              <h3 className="text-2xl font-extrabold">{s.title}</h3>
              <p className="text-[17px] leading-relaxed text-muted">{s.text}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* CHOOSE HOW TO HELP */}
      <Section className="pt-6 pb-14">
        <div className="flex flex-wrap items-center gap-10 rounded-[32px] bg-teal p-[clamp(28px,5vw,56px)] text-on-teal">
          <div className="flex flex-[1_1_360px] flex-col gap-3.5">
            <h2 className="text-[clamp(30px,3.5vw,44px)] font-black tracking-[-1px]">{t('home.help.title')}</h2>
            <p className="text-lg leading-normal text-teal-tint">{t('home.help.lead')}</p>
          </div>
          <div className="grid flex-[1_1_460px] grid-cols-[repeat(auto-fit,minmax(min(200px,100%),1fr))] gap-4">
            <div className="flex flex-col gap-2.5 rounded-[22px] bg-surface p-6 text-ink">
              <span className="text-[22px] font-black">{t('home.help.oneTitle')}</span>
              <span className="text-base leading-snug text-muted">{t('home.help.oneText')}</span>
              <Link
                to="/live"
                className="mt-auto flex justify-center rounded-[14px] bg-ink p-3.5 font-extrabold text-on-ink no-underline transition hover:opacity-90"
              >
                {t('home.help.oneCta')}
              </Link>
            </div>
            <div className="flex flex-col gap-2.5 rounded-[22px] bg-surface p-6 text-ink">
              <span className="text-[22px] font-black">{t('home.help.monthlyTitle')}</span>
              <span className="text-base leading-snug text-muted">{t('home.help.monthlyText')}</span>
              <DonateButton kind="monthly" className="mt-auto flex justify-center rounded-[14px] bg-teal p-3.5 font-extrabold text-on-teal transition hover:bg-teal-dark">
                {t('home.help.monthlyCta')}
              </DonateButton>
            </div>
          </div>
        </div>
      </Section>

      {/* GET THE APP */}
      <Section className="pt-6 pb-[72px]">
        <div className="flex flex-wrap items-center justify-between gap-8 rounded-[32px] bg-sand p-[clamp(28px,5vw,56px)]">
          <div className="flex flex-[1_1_380px] items-center gap-[22px]">
            <img src="/brand/urbanpurr-app-icon.svg" alt="" className="size-[88px] flex-none" />
            <div className="flex flex-col gap-1.5">
              <h2 className="text-[clamp(26px,3vw,36px)] font-black tracking-[-0.8px]">{t('home.app.title')}</h2>
              <p className="text-[17px] leading-normal text-muted">{t('home.app.text')}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3" title={t('common.comingSoon')}>
            <img src="/brand/badge-app-store.svg" alt={t('home.app.appStore')} className="h-14 w-auto" />
            <img src="/brand/badge-google-play.svg" alt={t('home.app.googlePlay')} className="h-14 w-auto" />
            <img src="/brand/badge-appgallery.svg" alt={t('home.app.appGallery')} className="h-14 w-auto" />
          </div>
        </div>
      </Section>

      <div className="mt-auto">
        <Footer />
      </div>
    </div>
  )
}
